/**
 * PWA chrome: the offline banner, the update prompt, and the install card.
 *
 * All three are rendered by MobileShell so every phone screen gets them without
 * each page having to opt in. They are deliberately separate components so a
 * future desktop shell can pick up the update prompt alone.
 */

import React from 'react';
import { CloudOff, Download, RefreshCw, Share, X } from 'lucide-react';
import useOnlineStatus from '../../pwa/useOnlineStatus';
import useInstallPrompt from '../../pwa/useInstallPrompt';

// ── Offline banner ────────────────────────────────────────────────────────────

/**
 * Persistent strip shown whenever requests are failing to reach the server.
 *
 * It is not dismissible on purpose. The app has no offline write queue — an
 * action taken while this is showing is lost, not deferred — so the tech needs
 * to keep seeing it, not tidy it away.
 */
export const OfflineBanner = () => {
    const online = useOnlineStatus();
    if (online) return null;

    return (
        <div
            role="status"
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white shrink-0"
        >
            <CloudOff size={15} className="shrink-0" />
            <p className="text-xs font-semibold leading-tight">
                No connection — changes won&rsquo;t save until you&rsquo;re back online.
            </p>
        </div>
    );
};

// ── Update prompt ─────────────────────────────────────────────────────────────

/**
 * Shown when a newer build has been downloaded and is waiting to take over.
 *
 * Applying is the user's call rather than automatic: a silent reload would
 * discard a half-typed note or an unrecorded measurement. Dismissing keeps the
 * old build running until the app is next launched cold.
 *
 * @param {Function} onApply    activates the waiting worker; the page reloads
 * @param {Function} onDismiss  hides the bar for this session
 */
export const UpdateBanner = ({ onApply, onDismiss }) => (
    <div className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white shrink-0">
        <RefreshCw size={15} className="shrink-0" />
        <p className="text-xs font-semibold leading-tight flex-1">
            A new version is ready.
        </p>
        <button
            onClick={onApply}
            className="px-3 py-1 rounded-lg bg-white/20 active:bg-white/30 text-xs font-bold"
        >
            Update
        </button>
        <button
            onClick={onDismiss}
            aria-label="Dismiss update"
            className="p-1 rounded-lg active:bg-white/20"
        >
            <X size={15} />
        </button>
    </div>
);

// ── Install card ──────────────────────────────────────────────────────────────

/**
 * Offers to add the app to the home screen. Renders nothing once installed,
 * once dismissed, or in a browser that has neither the install API nor the iOS
 * Share-sheet route.
 */
export const InstallCard = () => {
    const { canShow, instructionsOnly, promptInstall, dismiss } = useInstallPrompt();
    if (!canShow) return null;

    return (
        <div className="mx-4 mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
                    <span className="text-white font-display font-bold text-base">W</span>
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-emerald-900">Install Wadakaroo</p>
                    {instructionsOnly ? (
                        <p className="text-xs text-emerald-700 mt-0.5 leading-relaxed">
                            Tap <Share size={11} className="inline -mt-0.5" /> Share, then
                            &ldquo;Add to Home Screen&rdquo; to open it like an app.
                        </p>
                    ) : (
                        <p className="text-xs text-emerald-700 mt-0.5 leading-relaxed">
                            Add it to your home screen for full-screen access on the floor.
                        </p>
                    )}
                </div>
                <button
                    onClick={dismiss}
                    aria-label="Dismiss install prompt"
                    className="p-1 -mt-1 -mr-1 rounded-lg active:bg-emerald-100 shrink-0"
                >
                    <X size={16} className="text-emerald-600" />
                </button>
            </div>

            {!instructionsOnly && (
                <button
                    onClick={promptInstall}
                    className="mt-2.5 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl
                               bg-emerald-600 text-white text-sm font-semibold active:bg-emerald-700 min-h-[44px]"
                >
                    <Download size={15} /> Install
                </button>
            )}
        </div>
    );
};
